import { inject, Injectable } from '@angular/core';
import {
    ConfirmArchiveResult,
    defaultFormState,
    DeleteLinkResult,
    FormState,
    QueryResult,
    SearchOptionsModel,
} from '@app/data/models';
import { EmailTemplateModel } from '@app/data/models/email-template.model';
import { Action, createSelector, State, StateContext } from '@ngxs/store';
import { ManageEmailActions } from '../actions';
import { catchError, finalize, mergeMap, tap } from 'rxjs/operators';
import { ManageEmailTemplatesService } from '../services/manage-email-templates.service';
import { isFormValid } from '@app/shared/util';
import { HttpClient } from '@angular/common/http';
import { APP_ENVIRONMENT } from '@app/shared/tokens';
import { ServerResult } from '@app/shared/models';
import {
    errorHandler,
    getData,
    handleNotFoundError,
    transformResponseToText,
    verifyServerResult,
} from '@app/data/operators';
import { BlobService } from '@app/data/services/blob.service';
import { BlobContainer } from '@app/shared/enums';
import { Navigate } from '@ngxs/router-plugin';
import { NotificationActions } from '@app/data/actions';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '@app/shared/dialogs/confirmation-dialog.component';
import { Observable } from 'rxjs';
import { SendEmailPreviewDialogComponent } from '../dialogs/send-email-preview/send-email-preview-dialog.component';

interface StateModel {
    errors: Record<string, string>;
    searchOptions: SearchOptionsModel | null;
    searchResults: QueryResult<EmailTemplateModel> | null;

    isSaving: boolean;
    isLoadingTemplates: boolean;
    hasLoadedTemplates: boolean;
    emailTemplates: EmailTemplateModel[];

    copyModel: EmailTemplateModel | null;
    editModel: EmailTemplateModel | null;
    formEmailTemplate: FormState<EmailTemplateModel>;
}

@State<StateModel>({
    name: 'manageEmail',
    defaults: {
        errors: {},
        searchOptions: null,
        searchResults: null,

        isSaving: false,
        isLoadingTemplates: false,
        hasLoadedTemplates: false,
        emailTemplates: [],

        copyModel: null,
        editModel: null,
        formEmailTemplate: defaultFormState(),
    },
})
@Injectable()
export class ManageEmailState {
    http = inject(HttpClient);
    blobService = inject(BlobService);
    matDialog = inject(MatDialog);
    env = inject(APP_ENVIRONMENT);

    manageEmailTemplatesService = inject(ManageEmailTemplatesService);

    static hasLoadedLinks() {
        return createSelector([ManageEmailState], (state: StateModel) => state.hasLoadedTemplates);
    }

    static isLoading() {
        return createSelector([ManageEmailState], (state: StateModel) => state.isLoadingTemplates);
    }

    static getSearchResults() {
        return createSelector([ManageEmailState], (state: StateModel) => state.searchResults);
    }

    static getSearchOptions() {
        return createSelector([ManageEmailState], (state: StateModel) => state.searchOptions);
    }

    static getForm() {
        return createSelector([ManageEmailState], (state: StateModel) => state.formEmailTemplate);
    }

    static getEditModel() {
        return createSelector([ManageEmailState], (state: StateModel) => state.editModel);
    }

    static isFormValid() {
        return createSelector([ManageEmailState], (state: StateModel) => isFormValid(state.formEmailTemplate));
    }

    static isSaving() {
        return createSelector([ManageEmailState], (state: StateModel) => state.isSaving);
    }

    static getErrors() {
        return createSelector([ManageEmailState], (state: StateModel) => state.errors);
    }

    static getEmailTemplates() {
        return createSelector([ManageEmailState], (state: StateModel) => state.emailTemplates);
    }

    @Action(ManageEmailActions.LoadEmailTemplates)
    loadEmailTemplates({ dispatch, patchState }: StateContext<StateModel>) {
        patchState({
            isLoadingTemplates: true,
        });

        return this.manageEmailTemplatesService.getAllEmailTemplates().pipe(
            tap(emailTemplates => {
                patchState({
                    emailTemplates,
                });
            }),
            catchError(err => dispatch(new ManageEmailActions.LoadEmailTemplatesFailure(err))),
            finalize(() =>
                patchState({
                    hasLoadedTemplates: true,
                    isLoadingTemplates: false,
                }),
            ),
        );
    }

    @Action(ManageEmailActions.CopyTemplate)
    copyTemplate({ patchState, dispatch }: StateContext<StateModel>, { template }: ManageEmailActions.CopyTemplate) {
        return this.blobService.downloadBlobBlock(`email/templates/${template.rowKey}.html`, BlobContainer.Private).pipe(
            transformResponseToText(),
            handleNotFoundError('<p></p>'),
            tap(body => {
                patchState({
                    copyModel: {
                        ...template,
                        rowKey: '',
                        body,
                    },
                });

                dispatch(new Navigate(['/home/email-templates/new']));
            }),
        );
    }

    @Action(ManageEmailActions.LoadEmailTemplate)
    loadEmailTemplate(
        { getState, patchState, dispatch }: StateContext<StateModel>,
        { rowKey }: ManageEmailActions.LoadEmailTemplate,
    ): Observable<any> | void {
        const isNew = rowKey === 'new';
        const { copyModel } = getState();
        const editModel = isNew ? copyModel || getDefaultEmailTemplateModel() : null;

        patchState({
            editModel: null,
            copyModel: null,
        });

        if (!isNew) {
            return this.http
                .get<ServerResult<EmailTemplateModel>>(`${this.env.serverUrl}/manage/email-templates/${rowKey}`)
                .pipe(
                    verifyServerResult(),
                    getData(),
                    errorHandler(),
                    mergeMap(model => {
                        return this.blobService
                            .downloadBlobBlock(`email/templates/${rowKey}.html`, BlobContainer.Private)
                            .pipe(
                                transformResponseToText(),
                                handleNotFoundError('<p></p>'),
                                tap(body => {
                                    patchState({
                                        editModel: {
                                            ...model,
                                            body,
                                        },
                                    });
                                }),
                            );
                    }),
                );
        }

        patchState({
            editModel,
        });
    }

    @Action(ManageEmailActions.SaveEmailTemplate)
    saveEmailTemplate(
        { patchState, dispatch, getState }: StateContext<StateModel>,
        action: ManageEmailActions.SaveEmailTemplate,
    ) {
        const { formEmailTemplate: form } = getState();
        const { model } = form;

        if (!model) {
            return;
        }

        patchState({ isSaving: true });

        const content = model.body;

        return this.http
            .post<ServerResult<EmailTemplateModel>>(`${this.env.serverUrl}/manage/email-templates/save`, model)
            .pipe(
                verifyServerResult(),
                getData(),
                errorHandler(),
                mergeMap(template => {
                    template.body = content;

                    return dispatch([
                        new ManageEmailActions.SaveEmailTemplateContent(content, template.rowKey as string),
                        new Navigate(['/home/email-templates']),
                        new NotificationActions.Success('Template has been successfully saved'),
                    ]);
                }),
                catchError(err => dispatch(new ManageEmailActions.SaveEmailTemplateContentFailure(err))),
                finalize(() => {
                    patchState({ isSaving: false });
                }),
            );
    }

    @Action(ManageEmailActions.SaveEmailTemplateContent)
    saveEmailTemplateContent(
        { patchState }: StateContext<StateModel>,
        { content, rowKey }: ManageEmailActions.SaveEmailTemplateContent,
    ) {
        const name = `email/templates/${rowKey}.html`;

        return this.blobService.uploadBlobBlock(name, content, 'text/html', BlobContainer.Private);
    }

    @Action(ManageEmailActions.ConfirmDeleteTemplates)
    confirmDeleteTemplates(
        { dispatch }: StateContext<StateModel>,
        { templates }: ManageEmailActions.ConfirmDeleteTemplates,
    ) {
        return this.matDialog
            .open(ConfirmationDialogComponent, {
                disableClose: true,
                data: ConfirmArchiveResult({
                    title: 'Delete Template(s)',
                    message: `Are you sure you want to delete ${templates.length} template(s)?`,
                    confirmText: 'Delete',
                }),
            })
            .afterClosed()
            .pipe(
                tap(result => {
                    if (result) {
                        dispatch([
                            new ManageEmailActions.DeleteTemplates(templates?.map(r => r.rowKey || '') || []),
                            new Navigate(['/home/email-templates']),
                        ]);
                    }
                }),
            );
    }

    @Action(ManageEmailActions.DeleteTemplates)
    deleteTemplates({ patchState, dispatch }: StateContext<StateModel>, { rowKeys }: ManageEmailActions.DeleteTemplates) {
        patchState({
            isSaving: true,
        });

        return this.http
            .request<
                ServerResult<{
                    objectIdsToRemove: string[];
                }>
            >('DELETE', `${this.env.serverUrl}/manage/email-templates`, {
                body: rowKeys,
            })
            .pipe(
                verifyServerResult(),
                getData(),
                errorHandler(),
                mergeMap(result => {
                    return dispatch([
                        new NotificationActions.Success('Template(s) has been successfully deleted'),
                        new ManageEmailActions.SearchTemplates(),
                    ]);
                }),
                catchError(err => dispatch(new ManageEmailActions.DeleteTemplatesFailure(err))),
                finalize(() =>
                    patchState({
                        isSaving: false,
                    }),
                ),
            );
    }

    @Action(ManageEmailActions.SearchTemplates)
    searchTemplates(
        { patchState, getState, dispatch }: StateContext<StateModel>,
        action: ManageEmailActions.SearchTemplates,
    ) {
        const { searchOptions: options } = getState();
        const model = action.model ?? options ?? { total: undefined, filter: '', tags: [], offset: 0, limit: 25 };

        if (model.total === undefined) {
            model.total = options?.total;
        }

        patchState({ isLoadingTemplates: true });

        return this.manageEmailTemplatesService.searchTemplates(model).pipe(
            tap(queryResult => {
                patchState({
                    searchResults: queryResult,
                    searchOptions: {
                        filter: model.filter,
                        tags: model.tags,
                        limit: queryResult.limit,
                        total: queryResult.total,
                        offset: queryResult.offset,
                    },
                });
            }),
            catchError(err => dispatch(new ManageEmailActions.SearchTemplatesFailure(err))),
            finalize(() => {
                patchState({ isLoadingTemplates: false, hasLoadedTemplates: true });
            }),
        );
    }

    @Action(ManageEmailActions.ShowPreviewEmail)
    showPreviewEmail({ dispatch }: StateContext<StateModel>, { model }: ManageEmailActions.ShowPreviewEmail) {
        return this.matDialog.open(SendEmailPreviewDialogComponent, {
            data: model,
            width: '80%',
        });
    }
}

function getDefaultEmailTemplateModel(): EmailTemplateModel {
    return {
        rowKey: '',
        subject: '',
        body: '',
        createdDate: new Date(),
    };
}
